import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SCHEDULE_C_CATEGORIES } from "@/lib/api";
import { Sparkles, Check, X } from "lucide-react";

export default function ConfirmCard({ data, onSave, onCancel, saving }) {
  const [vendor, setVendor] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [category, setCategory] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (data) {
      setVendor(data.vendor || "");
      setAmount(data.amount != null ? String(data.amount) : "");
      setDate(data.date || "");
      setCategory(data.category || "Other expenses");
      setNotes("");
    }
  }, [data]);

  const submit = () => {
    onSave({ vendor, amount: parseFloat(amount || 0), date, category, notes });
  };

  return (
    <div data-testid="confirm-card" className="rounded-2xl border border-primary/20 bg-primary/5 p-6 sm:p-8 animate-fade-up">
      <div className="flex items-center gap-2 mb-6">
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">AI extracted · confirm to save</span>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label className="text-xs font-semibold">Vendor</Label>
          <Input data-testid="confirm-vendor-input" value={vendor} onChange={(e) => setVendor(e.target.value)} className="bg-background h-11 rounded-xl" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold">Amount ($)</Label>
          <Input data-testid="confirm-amount-input" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-background h-11 rounded-xl" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold">Date</Label>
          <Input data-testid="confirm-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-background h-11 rounded-xl" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold">Schedule C category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="confirm-category-select" className="bg-background h-11 rounded-xl">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {SCHEDULE_C_CATEGORIES.map((c) => (
                <SelectItem key={c} value={c} data-testid={`category-option-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label className="text-xs font-semibold">Notes (optional)</Label>
          <Input data-testid="confirm-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-background h-11 rounded-xl" placeholder="e.g. client lunch, gas for rides" />
        </div>
      </div>

      <div className="flex gap-3 mt-7">
        <Button data-testid="save-receipt-button" onClick={submit} disabled={saving} className="flex-1 h-11 rounded-xl bg-primary hover:bg-primary/90 transition-colors duration-200 font-semibold">
          <Check className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Confirm & save"}
        </Button>
        <Button data-testid="cancel-confirm-button" variant="outline" onClick={onCancel} className="h-11 rounded-xl">
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
